/**
 * Autocomplete data + logic. Sources are merged from the document's <lists>
 * and scanned from the script body (character names, sluglines). The pure
 * computeSuggestions() decides what to offer given the current element, text,
 * and caret — the plugin/UI in autocomplete.ts only renders and applies it.
 */
import type { Para } from "../format/types";

export interface SuggestionSources {
  sceneIntros: string[];
  sceneTimes: string[];
  locations: string[];
  characters: string[];
  extensions: string[];
  transitions: string[];
}

export interface SuggestContext {
  element: string;
  text: string;
  caret: number;
  sources: SuggestionSources;
  sceneTimeSeparator: string;
}

export interface SuggestResult {
  items: string[];
  /** Text offsets [from, to) that an accepted item replaces. */
  from: number;
  to: number;
}

const MAX_ITEMS = 6;

function uniquePush(set: Set<string>, value: string): void {
  const v = value.trim();
  if (v) set.add(v);
}

/** Read the <lists> autocomplete sources and scan the body for more. */
export function collectSources(
  dom: Document,
  paras: Para[],
  sceneTimeSeparator: string,
): SuggestionSources {
  const readList = (parentTag: string, childTag: string): string[] => {
    const parent = dom.getElementsByTagName(parentTag)[0];
    if (!parent) return [];
    const out: string[] = [];
    const els = parent.getElementsByTagName(childTag);
    for (let i = 0; i < els.length; i++) {
      const name = els[i]!.getAttribute("name");
      if (name) out.push(name);
    }
    return out;
  };

  const sceneIntros = new Set<string>(readList("scene_intros", "scene_intro"));
  const sceneTimes = new Set<string>(readList("scene_times", "scene_time"));
  const extensions = new Set<string>(readList("extensions", "extension"));
  const transitions = new Set<string>(readList("transitions", "transition"));
  const locations = new Set<string>(readList("locations", "location"));
  const characters = new Set<string>();

  const sep = sceneTimeSeparator;
  for (const para of paras) {
    const text = para.runs.map((r) => r.text).join("");
    if (para.style === "Character") {
      uniquePush(characters, stripCharacterSuffix(text));
    } else if (para.style === "Scene Heading") {
      scanSlugline(text, sep, sceneIntros, locations, sceneTimes);
    } else if (para.style === "Transition") {
      uniquePush(transitions, text);
    }
  }

  return {
    sceneIntros: [...sceneIntros],
    sceneTimes: [...sceneTimes],
    locations: [...locations],
    characters: [...characters],
    extensions: [...extensions],
    transitions: [...transitions],
  };
}

/** Strip trailing (CONT'D) and extensions from a Character line. */
export function stripCharacterSuffix(text: string): string {
  let t = text.trim();
  // Remove trailing parenthetical groups repeatedly: "MIĆA (CONT'D) (V.O.)" → "MIĆA".
  let prev: string;
  do {
    prev = t;
    t = t.replace(/\s*\([^)]*\)\s*$/u, "").trim();
  } while (t !== prev);
  return t;
}

/** Parse a slugline "S1. ENT. LOCATION - TIME" into intro/location/time. */
function scanSlugline(
  text: string,
  sep: string,
  intros: Set<string>,
  locations: Set<string>,
  times: Set<string>,
): void {
  // Drop a manual scene number prefix like "S1." / "12.".
  let body = text.replace(/^\s*S?\d+[.)]?\s*/iu, "").trim();
  if (!body) return;

  // Intro = first whitespace-delimited token if it ends with "." or "/".
  const firstSpace = body.indexOf(" ");
  const firstToken = firstSpace === -1 ? body : body.slice(0, firstSpace);
  if (/[./]$/.test(firstToken)) {
    uniquePush(intros, firstToken.toLocaleUpperCase("sr-Latn"));
    body = firstSpace === -1 ? "" : body.slice(firstSpace + 1);
  }

  const sepIndex = body.indexOf(sep);
  if (sepIndex >= 0) {
    uniquePush(locations, body.slice(0, sepIndex));
    uniquePush(times, body.slice(sepIndex + sep.length));
  } else if (body) {
    uniquePush(locations, body);
  }
}

/** The token immediately before the caret (from the last space). */
function currentToken(text: string, caret: number): { token: string; start: number } {
  const before = text.slice(0, caret);
  const lastSpace = before.lastIndexOf(" ");
  return { token: before.slice(lastSpace + 1), start: lastSpace + 1 };
}

function match(items: string[], query: string): string[] {
  const q = query.toLocaleLowerCase("sr-Latn");
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    const lower = item.toLocaleLowerCase("sr-Latn");
    if (lower.startsWith(q) && lower !== q && !seen.has(item)) {
      seen.add(item);
      out.push(item);
      if (out.length >= MAX_ITEMS) break;
    }
  }
  return out;
}

function result(items: string[], from: number, to: number): SuggestResult | null {
  return items.length > 0 ? { items, from, to } : null;
}

/** Decide autocomplete suggestions for the current editing context. */
export function computeSuggestions(ctx: SuggestContext): SuggestResult | null {
  const { element, text, caret, sources, sceneTimeSeparator: sep } = ctx;

  if (element === "Scene Heading") {
    const sepIndex = text.indexOf(sep);
    // After the separator → scene times.
    if (sepIndex >= 0 && caret >= sepIndex + sep.length) {
      const segStart = sepIndex + sep.length;
      const query = text.slice(segStart, caret);
      if (query.trim().length === 0) return null;
      return result(match(sources.sceneTimes, query.trimStart()), segStart + (query.length - query.trimStart().length), caret);
    }
    const { token, start } = currentToken(text, caret);
    if (token.length === 0) return null;
    // Offer intros and locations for the current token.
    const items = [...match(sources.sceneIntros, token), ...match(sources.locations, token)];
    return result(dedupe(items).slice(0, MAX_ITEMS), start, caret);
  }

  if (element === "Character") {
    const { token, start } = currentToken(text, caret);
    if (token.startsWith("(")) {
      return result(match(sources.extensions, token), start, caret);
    }
    // Match the whole line prefix (character names can contain spaces).
    const query = text.slice(0, caret);
    if (query.length === 0) return null;
    return result(match(sources.characters, query), 0, caret);
  }

  if (element === "Transition") {
    const query = text.slice(0, caret);
    if (query.length === 0) return null;
    return result(match(sources.transitions, query), 0, caret);
  }

  return null;
}

function dedupe(items: string[]): string[] {
  return [...new Set(items)];
}
